import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Shield, Globe, Coins, TrendingUp, Users } from 'lucide-react';
import PageTransition from '../components/PageTransition';

const Home = () => {
  const exchanges = [
    { name: 'Binance', type: 'CEX', logo: '/exchanges/binance.png' },
    { name: 'Coinbase', type: 'CEX', logo: '/exchanges/coinbase.png' },
    { name: 'OKX', type: 'CEX', logo: '/exchanges/okx.png' },
    { name: 'Kucoin', type: 'CEX', logo: '/exchanges/kucoin.png' },
    { name: 'Kraken', type: 'CEX', logo: '/exchanges/kraken.png' },
    { name: 'Bybit', type: 'CEX', logo: '/exchanges/bybit.png' },
    { name: 'Uniswap', type: 'DEX', logo: '/exchanges/uniswap.png' },
    { name: 'PancakeSwap', type: 'DEX', logo: '/exchanges/pancakeswap.png' },
    { name: 'Jupiter', type: 'DEX', logo: '/exchanges/jupiter.png' },
    { name: 'Raydium', type: 'DEX', logo: '/exchanges/raydium.png' },
  ];

  const stats = [
    { label: 'Holders', value: '50,000+', icon: Users },
    { label: 'Market Cap', value: '$10M+', icon: TrendingUp },
    { label: 'Transactions', value: '1M+', icon: Zap },
  ];

  const ecosystem = [
    { icon: Coins, title: 'Dobix Wallet', desc: 'Secure multi-chain wallet with built-in swap' },
    { icon: Shield, title: 'Dobix Staking', desc: 'Stake your tokens and earn passive rewards' },
    { icon: Globe, title: 'Dobix Pay', desc: 'Pay anywhere with crypto, convert to fiat instantly' },
    { icon: Zap, title: 'Dobix Bot', desc: 'Trade directly from Telegram with our trading bot' },
  ];

  const newsItems = [
    'Dobix listed on major exchanges',
    'New partnership announced',
    'Staking rewards increased to 12% APY',
    'Mobile app launching soon',
    'Community reaches 50,000 holders',
    'Dobix Pay beta testing begins',
  ];

  return (
    <PageTransition>
      <div className="min-h-screen text-white bg-[#0A0A0A]">

        {/* Hero Section */}
        <section className="relative pt-28 pb-16 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FF6B00]/5 via-transparent to-transparent" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#FF6B00]/10 rounded-full blur-[150px] opacity-30" />

          <div className="relative max-w-7xl mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6 relative inline-block"
              >
                {/* Orange glow behind logo */}
                <div className="absolute inset-0 bg-[#FF6B00] rounded-full blur-3xl opacity-50 animate-pulse" />

                {/* Logo */}
                <img
                  src="/dobix-logo.png"
                  alt="Dobix Coin"
                  className="relative h-28 w-28 mx-auto float"
                />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-4xl md:text-6xl font-bold mb-4 font-heading"
              >
                The People's
                <br />
                <span className="text-gradient">Cryptocurrency</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto"
              >
                DOBIX is the utility token powering the next generation of decentralized payments, messaging, and DeFi on Solana.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-3 justify-center"
              >
                <Link to="/wallet">
                  <button className="bg-[#FF6B00] hover:bg-[#FF8C00] text-white font-medium px-6 py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:shadow-[#FF6B00]/20">
                    Buy Dobix
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/features">
                  <button className="border border-[#FF6B00]/50 hover:border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00]/10 font-medium px-6 py-2.5 rounded-lg text-sm transition-all">
                    Explore Ecosystem
                  </button>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* News Ticker */}
        <div className="ticker-wrapper py-3">
          <div className="ticker-content">
            {[...newsItems, ...newsItems].map((item, index) => (
              <div key={index} className="flex items-center px-6 whitespace-nowrap">
                <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full mr-2" />
                <span className="text-gray-400 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="stat-card text-center"
                >
                  <stat.icon className="w-8 h-8 text-[#FF6B00] mx-auto mb-3" />
                  <p className="text-3xl font-bold text-gradient mb-1">{stat.value}</p>
                  <p className="text-gray-400 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Ecosystem Section */}
        <section className="py-16 px-4 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF6B00]/5 to-transparent" />
          <div className="relative max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-3 font-heading">
                The Dobix <span className="text-gradient">Ecosystem</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-sm">
                A complete suite of products designed to make crypto accessible to everyone.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {ecosystem.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-dark card-hover rounded-xl p-5"
                >
                  <div className="w-11 h-11 bg-gradient-to-br from-[#FF6B00] to-[#FFB800] rounded-lg flex items-center justify-center mb-3">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-xs">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Where to Buy Section - Sliding Exchanges */}
        <section className="py-16 px-4 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-3 font-heading">
                Where to Buy <span className="text-gradient">Dobix</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-sm">
                DOBIX is available on the world's leading exchanges.
              </p>
            </motion.div>

            {/* Sliding Exchange Ticker */}
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0A0A0A] to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0A0A0A] to-transparent z-10" />

              <div className="flex overflow-hidden">
                <motion.div
                  animate={{ x: [0, -50 * exchanges.length] }}
                  transition={{
                    x: {
                      repeat: Infinity,
                      repeatType: "loop",
                      duration: 18,
                      ease: "linear",
                    },
                  }}
                  className="flex gap-3"
                >
                  {[...exchanges, ...exchanges, ...exchanges].map((exchange, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 flex items-center gap-3 bg-[#111] hover:bg-[#1a1a1a] border border-[#222] hover:border-[#FF6B00]/50 rounded-xl px-4 py-3 cursor-pointer transition-all group"
                    >
                      <img
                        src={exchange.logo}
                        alt={exchange.name}
                        className="w-8 h-8 object-contain"
                      />
                      <div>
                        <p className="font-medium text-white text-sm group-hover:text-[#FF6B00] transition-colors">{exchange.name}</p>
                        <p className="text-[10px] text-gray-500">{exchange.type}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>

            <div className="text-center mt-8">
              <Link to="/wallet">
                <button className="border border-[#FF6B00]/50 hover:border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00]/10 font-medium px-5 py-2 rounded-lg text-sm transition-all">
                  See All Exchanges
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-dark rounded-2xl p-8 md:p-10 text-center relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B00]/10 via-transparent to-[#FFB800]/10" />
              <div className="relative">
                <img
                  src="/dobix-logo.png"
                  alt="Dobix"
                  className="h-16 w-16 mx-auto mb-4"
                />
                <h2 className="text-2xl md:text-3xl font-bold mb-3 font-heading">
                  Join the Dobix Community
                </h2>
                <p className="text-gray-400 mb-6 max-w-lg mx-auto text-sm">
                  Be part of the most active and welcoming crypto community. Stay updated, participate in governance, and help shape the future.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <a href="#" className="bg-[#FF6B00] hover:bg-[#FF8C00] text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                    Follow on X
                  </a>
                  <a href="#" className="border border-[#333] hover:border-[#FF6B00]/50 text-gray-300 hover:text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg>
                    Discord
                  </a>
                  <a href="#" className="border border-[#333] hover:border-[#FF6B00]/50 text-gray-300 hover:text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 0 0-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" /></svg>
                    Telegram
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Home;